import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUserStore } from '@/stores/userStore';
import { getCachedParticipant } from '@/services/database';
import { LoadingPage } from '@/components/ui/Spinner';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';

export default function TicketPage() {
  const navigate = useNavigate();
  const { participantId } = useParams();
  const { participant: storedParticipant, setParticipant } = useUserStore();
  const ticketRef = useRef<HTMLDivElement>(null);

  const [participant, setLocalParticipant] = useState(storedParticipant);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

useEffect(() => {
  const loadParticipant = async () => {
    if (participantId && participantId !== storedParticipant?.id) {
      setLoading(true);
      try {
        const data = await getCachedParticipant(participantId);
        if (data) {
          setLocalParticipant(data);
          setParticipant(data);
        } else {
          setError('Ticket not found. Please check your email for the correct link.');
        }
      } catch (err) {
        console.error('Error loading participant:', err);
        setError('Failed to load ticket. Please try again.');
      } finally {
        setLoading(false);
      }
    } else if (!storedParticipant) {
      navigate('/');
    } else {
      setLocalParticipant(storedParticipant);
    }
  };

  loadParticipant();
}, [participantId, storedParticipant, setParticipant, navigate]);

  const handleDownload = async () => {
    if (!ticketRef.current || downloading) return;

    setDownloading(true);
    try {
      const canvas = await html2canvas(ticketRef.current, {
        scale: 2,
        backgroundColor: '#faf5e4',
        logging: false,
      });

      const link = document.createElement('a');
      link.download = `Greater-Heights-Ticket-${participant?.fullName.replace(/\s+/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download ticket. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return <LoadingPage message="Loading your ticket..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cream via-white to-cream/50 flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-white/80 backdrop-blur-xl rounded-3xl shadow-large border border-white p-12 text-center">
          <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-6">
            <svg className="w-10 h-10 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-2xl font-display font-bold text-earth-brown mb-4">Ticket Not Found</h2>
          <p className="text-earth-brown/70 mb-8">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full px-6 py-4 bg-crimson text-white font-semibold rounded-2xl hover:bg-crimson-dark hover:shadow-large transition-all duration-300 active:scale-95"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (!participant) {
    return <LoadingPage message="Loading..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream via-white to-cream/50">
      {/* Back to Home Button */}
      <div className="fixed top-6 left-6 z-50">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2.5 bg-white/80 backdrop-blur-lg hover:bg-white text-earth-brown rounded-xl shadow-soft hover:shadow-medium transition-all duration-300 group border border-earth-tan/20"
        >
          <svg 
            className="w-5 h-5 transition-transform group-hover:-translate-x-1" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span className="font-medium text-sm">Back to Home</span>
        </button>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-20 md:py-32">
        {/* Email Verification Warning */}
        {!participant.emailVerified && (
          <div className="mb-8 bg-yellow-50 border-l-4 border-yellow-400 rounded-2xl p-6 shadow-soft">
            <div className="flex items-start gap-4">
              <svg className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <h3 className="text-sm font-bold text-yellow-900 mb-1">
                  ⚠️ Email Verification Required
                </h3>
                <p className="text-sm text-yellow-800 leading-relaxed">
                  Please verify your email to activate this ticket. Check your inbox for the verification link.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Premium Event Ticket */}
        <div ref={ticketRef} className="relative">
          {/* Ticket Container */}
          <div className="bg-white rounded-3xl shadow-large overflow-hidden border-4 border-crimson">
            {/* Ticket Header - Maroon Background */}
            <div className="relative bg-gradient-to-r from-crimson to-crimson-dark p-8 text-white">
              {/* Decorative Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                  backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                  backgroundSize: '30px 30px'
                }} />
              </div>

              <div className="relative z-10">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h1 className="text-4xl font-display font-bold mb-2">Greater Heights</h1>
                    <p className="text-cream/90 text-lg">Holy Week Retreat 2026</p>
                  </div>
                  <div className="text-right">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm font-semibold">ADMIT ONE</span>
                    </div>
                  </div>
                </div>

                {/* Event Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-cream/70 text-sm mb-1">Date</p>
                    <p className="font-semibold text-lg">April 2-3, 2026</p>
                  </div>
                  <div>
                    <p className="text-cream/70 text-sm mb-1">Venue</p>
                    <p className="font-semibold text-lg">Matrix Creation</p>
                  </div>
                </div>
              </div>

              {/* Decorative Notches */}
              <div className="absolute -bottom-3 left-0 w-6 h-6 bg-cream rounded-full" />
              <div className="absolute -bottom-3 right-0 w-6 h-6 bg-cream rounded-full" />
            </div>

            {/* Dotted Separator */}
            <div className="relative h-1">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t-2 border-dashed border-earth-tan/30" />
              </div>
            </div>

            {/* Ticket Body */}
            <div className="p-8">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Left Side - Participant Info */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xs uppercase tracking-widest text-earth-brown/50 font-semibold mb-3">
                      Participant Details
                    </h3>
                    
                    <div className="space-y-4">
                      <InfoRow label="Name" value={participant.fullName} />
                      <InfoRow label="Email" value={participant.email} />
                      <InfoRow label="Age" value={String(participant.age ?? '')} />
                      <InfoRow 
                        label="Ticket ID" 
                        value={participant.id.slice(0, 12).toUpperCase()} 
                        mono
                      />
                    </div>
                  </div>

                  <div className="pt-6 border-t border-earth-tan/30">
                    <div className="bg-crimson/10 rounded-2xl p-4 border-2 border-crimson/20">
                      <p className="text-xs uppercase tracking-wide text-earth-brown/60 font-semibold mb-2">
                        Check-in PIN
                      </p>
                      <p className="text-3xl font-bold font-mono text-crimson tracking-wider">
                        {participant.checkInPin}
                      </p>
                      <p className="text-xs text-earth-brown/60 mt-2">
                        Present this PIN during check-in
                      </p>
                    </div>
                  </div>

                  <div className="pt-4">
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${
                      participant.emailVerified 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {participant.emailVerified ? (
                        <>
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Verified
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                          </svg>
                          Pending Verification
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Side - QR Code */}
                <div className="flex flex-col items-center justify-center">
                  <div className="bg-white p-6 rounded-2xl border-4 border-crimson shadow-medium">
                    <QRCodeSVG
                      value={JSON.stringify({
                        id: participant.id,
                        event: 'greater-heights-2026',
                        timestamp: participant.registrationTimestamp,
                      })}
                      size={220}
                      level="H"
                      includeMargin={true}
                      bgColor="#faf5e4"
                      fgColor="#b43c3a"
                    />
                  </div>
                  <p className="text-xs text-center text-earth-brown/60 mt-4 max-w-xs">
                    Present this QR code at the registration desk for quick check-in
                  </p>
                </div>
              </div>
            </div>

            {/* Ticket Footer */}
            <div className="bg-cream border-t-2 border-dashed border-earth-tan/30 px-8 py-6">
              <div className="flex items-center justify-between text-xs text-earth-brown/60">
                <p>Registered: {participant.registrationTimestamp ? new Date(participant.registrationTimestamp).toLocaleDateString() : ''}</p>
                <p className="font-mono">#{participant.id.slice(0, 8).toUpperCase()}</p>
              </div>
            </div>
          </div>

          {/* Decorative Corner Cuts */}
          <div className="absolute -top-2 -left-2 w-8 h-8 bg-cream rounded-tl-3xl" />
          <div className="absolute -top-2 -right-2 w-8 h-8 bg-cream rounded-tr-3xl" />
          <div className="absolute -bottom-2 -left-2 w-8 h-8 bg-cream rounded-bl-3xl" />
          <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-cream rounded-br-3xl" />
        </div>

        {/* Action Buttons */}
        <div className="mt-10 grid md:grid-cols-2 gap-4">
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="px-8 py-5 bg-crimson text-white font-bold rounded-2xl hover:bg-crimson-dark hover:shadow-large transition-all duration-300 flex items-center justify-center gap-3 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {downloading ? (
              <>
                <svg className="w-6 h-6 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Downloading...
              </>
            ) : (
              <>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Ticket
              </>
            )}
          </button>

          <button
            onClick={() => navigate('/day1')}
            className="px-8 py-5 bg-white text-earth-brown font-bold rounded-2xl border-2 border-earth-tan/50 hover:border-earth-tan hover:bg-earth-tan/5 transition-all duration-300 flex items-center justify-center gap-3"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            View Event Schedule
          </button>
        </div>

        {/* Important Notes */}
        <div className="mt-10 bg-white/80 backdrop-blur-xl rounded-3xl shadow-soft border border-white p-8">
          <h3 className="text-xl font-display font-bold text-earth-brown mb-6 flex items-center gap-2">
            <svg className="w-6 h-6 text-crimson" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            Important Reminders
          </h3>
          <ul className="space-y-4">
            <ReminderItem text="Save a screenshot or download this ticket before the event" />
            <ReminderItem text="Bring a valid government-issued ID for verification" />
            <ReminderItem text="Memorize or write down your 4-digit PIN" />
            <ReminderItem text="Arrive at least 30 minutes early for smooth check-in" />
            <ReminderItem text="This ticket admits one person only" />
          </ul>
        </div>
      </div>
    </div>
  );
}

const InfoRow = ({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) => (
  <div>
    <p className="text-xs uppercase tracking-wide text-earth-brown/50 font-semibold mb-1">{label}</p>
    <p className={`text-base font-semibold text-earth-brown ${mono ? 'font-mono text-sm' : ''}`}>
      {value}
    </p>
  </div>
);

const ReminderItem = ({ text }: { text: string }) => (
  <li className="flex items-start gap-3">
    <svg className="w-5 h-5 text-crimson flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
    <span className="text-earth-brown/80 leading-relaxed">{text}</span>
  </li>
);